import DisplayObserver from './display.js';

class PageViewObserver {

  constructor(observer, sessionId) {

    this.observer = observer;
    this.sessionId = sessionId;
    this.pageViewStatusTimerReference = null;
    this.pageSessionId = null;
    this.genericClickHandler = this.genericClickHandler.bind(this);
    this.displayObserver = new DisplayObserver(this);
    this.stopped = false;

    this.queue = [];
    this.isProcessingQueue = false;

  }

  async processQueue() {

    if (this.isProcessingQueue || this.queue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.queue.length > 0) {
      const { payload, url, method, responseHandler, resolve, reject } = this.queue.shift();

      try {
        const response = await fetch(url, { method, body: JSON.stringify(payload) });
        const result = await responseHandler(response);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    this.isProcessingQueue = false;
  }

  queueRequest(url, payload, method = 'POST', responseHandler = null) {
    return new Promise((resolve, reject) => {
      this.queue.push({ url, payload, method, responseHandler, resolve, reject });
      this.processQueue();
    });
  }

  getObserverId() {

    return this.observer.getObserverId();

  }

  getSiteSessionId() {

    return this.sessionId;

  }

  setSiteSessionId(sessionId) {
    this.sessionId = sessionId;
  }

  determineReferrerAndReferrerDomain() {

    const result = {
      referrer: document.referrer || 'Direct'
    };

    if (result.referrer === 'Direct') {
      result.referrerDomain = 'Direct';
    } else {
      const url = new URL(result.referrer);
      result.referrerDomain = url.host;
    }

    return result;

  }

  buildDefaultPageEventPayload() {

    const payload = {
      observerId: this.getObserverId(),
      sessionId: this.getSiteSessionId(),
      url: window.location.href,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      userAgent: window.navigator.userAgent,
      ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...this.determineReferrerAndReferrerDomain()
    };

    return payload;

  }

  buildPageViewEventPayload() {

    const payload = {
      ...this.buildDefaultPageEventPayload(),
      type: 'PAGE_VIEW',
      metadata: {
        userType: this.observer.userType,
        viewDurationInMilliseconds: 0,
        scrollDepth: 0
      }
    };

    return payload;

  }

  tryReinitializeSession(siteSessionId) {

    if (this.getSiteSessionId() === siteSessionId) {
      return;
    }
    if (this.stopped === true) {
      return;
    }

    this.stop();

    this.observer.doStart(siteSessionId);

  }

  async processPageEventResponse(response) {

    if (response.status !== 200) {
      return;
    }

    const result = await response.json();

    this.tryReinitializeSession(result.sessionId);

    return result;

  }

  async updatePageViewStatus() {

    if (this.stopped === true) {
      return;
    }

    const payload = {
      ...this.buildDefaultPageEventPayload(),
      type: 'PAGE_VIEW_STATUS',
      pageSessionId: this.pageSessionId,
      metadata: {
        scrollDepth: this.calculateScrollPercentage()
      }
    };

    const url = this.observer.pageStatusPath;

    return this.queueRequest(url, payload, 'POST', async (response) => {

      if (this.stopped === true) {
        return;
      }

      const result = await response.json();

      this.trackPageViewStatus();

      const customResponse = {
        status: 200,
        async json() {
          return { sessionId: result._id };
        }
      };
      this.processPageEventResponse(customResponse);

    });

  }

  trackPageViewStatus() {

    this.pageViewStatusTimerReference = setTimeout(this.updatePageViewStatus.bind(this), 1000 * 10);

  }

  async emit(type, metadata) {

    const payload = this.buildCustomEventPayload(type, metadata);

    const url = this.observer.eventsPath;

    const response = await fetch(url, { method: 'POST', body: JSON.stringify(payload) });

    return this.processPageEventResponse(response);

  }

  async emitDisplayEvent(data, durationInMilliseconds) {

    const url = this.observer.displayPath;
    const payload = {
      sessionId: this.getSiteSessionId(),
      observerId: this.getObserverId(),
      pageSessionId: this.pageSessionId,
      events: data,
      url: window.location.href,
      durationInMilliseconds
    };

    return this.queueRequest(url, payload, 'POST', this.processPageEventResponse.bind(this));

  }

  async emitPageEvent(payload) {

    const url = this.observer.eventsPath;

    return this.queueRequest(url, payload, 'POST', this.processPageEventResponse.bind(this));

  }

  async emitPageViewEvent() {

    const payload = this.buildPageViewEventPayload();

    const url = this.observer.eventsPath;

    const response = await fetch(url, { method: 'POST', body: JSON.stringify(payload) });

    if (response.status !== 200) {
      return;
    }

    const result = await response.json();

    this.pageSessionId = result.pageSessionId;

  }

  calculateScrollPercentage() {
    // Get the current scroll position
    var scrollPosition = window.scrollY || window.pageYOffset;

    if (scrollPosition === 0) {
      return 0;
    }

    // Get the scroll height
    var scrollHeight = document.documentElement.scrollHeight;

    // Get the viewport height
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Calculate the percentage of scroll
    var scrollPercentage = (scrollPosition / (scrollHeight - viewportHeight)) * 100;

    return Math.floor(scrollPercentage);
  }

  trackPageScrollDepth() {

  }

  getElementDescriptor(element) {

    // Get tag name
    let name = element.tagName.toLowerCase();

    // Get name attribute, if exists
    let nameAttr = element.getAttribute('name') ? `[name="${element.getAttribute('name')}"]` : '';

    // Get ID
    let idString = element.id ? '#' + element.id : '';

    // Get classes
    let classString = '';
    if (element.classList.length > 0) {
      classString = '.' + Array.from(element.classList).join('.');
    }

    return name + nameAttr + idString + classString;

  }

  getAnchorParent(element) {

    if (element.tagName === 'A') {
      return element; // Found an anchor parent
    }

    // Start with the parent node of the given element
    let parent = element.parentNode;

    // Continue traversing upward through the DOM tree until we reach the document root (null)
    while (parent !== null) {
      // Check if the current parent is an anchor tag
      if (parent.tagName === 'A') {
        return parent; // Found an anchor parent
      }

      // Move up to the next parent node
      parent = parent.parentNode;
    }

    // If no anchor parent is found, return false
    return null;
  }

  getClickTarget(element) {

    const anchor = this.getAnchorParent(element);

    if (anchor === null) {
      return element;
    }

    return anchor;

  }

  getClickActionDetails(event) {

    const element = event.target;
    const target = this.getClickTarget(element);
    const tagName = target.tagName.toUpperCase();
    const content = target.textContent.trim();
    const href = target.getAttribute('href');

    return {
      content,
      target: this.getElementDescriptor(element),
      tagName,
      href,
      'Click Type': content || href || ''
    };

  }

  buildClickEventPayload(event) {

    const payload = {
      ...this.buildDefaultPageEventPayload(),
      type: 'Click',
      metadata: this.getClickActionDetails(event)
    };

    return payload;

  }

  genericClickHandler(event) {

    const payload = this.buildClickEventPayload(event);

    this.emitPageEvent(payload);

  }

  registerClickHandler() {

    document.body.addEventListener('click', this.genericClickHandler);

  }

  unregisterClickHandler() {

    document.body.removeEventListener('click', this.genericClickHandler);

  }

  async start() {

    if (this.observer.dryRun === true) {
      return;
    }

    await this.emitPageViewEvent();

    this.displayObserver.start();

    this.trackPageViewStatus();
    this.trackPageScrollDepth();

    this.registerClickHandler();

  }

  stopPageViewStatusUpdating() {

    if (this.pageViewStatusTimerReference !== null) {
      clearTimeout(this.pageViewStatusTimerReference);
      this.pageViewStatusTimerReference = null;
    }

  }

  async stop() {

    this.stopped = true;

    if (this.observer.dryRun === true) {
      return;
    }

    this.displayObserver.stop();
    this.stopPageViewStatusUpdating();
    this.unregisterClickHandler();

  }

  buildSignupEventPayload(Source) {

    const payload = {
      ...this.buildDefaultPageEventPayload(),
      type: 'Signup',
      metadata: {
        Source
      }
    };

    return payload;

  }

  async emitSignupEvent(source) {

    const payload = this.buildSignupEventPayload(source);

    await this.emitPageEvent(payload);

  }

  buildPaidUserEventPayload(Product) {

    const payload = {
      ...this.buildDefaultPageEventPayload(),
      type: 'Paid User',
      metadata: {
        'Plan Type': Product
      }
    };

    return payload;

  }

  async emitPaidUserEvent(product) {

    const payload = this.buildPaidUserEventPayload(product);

    await this.emitPageEvent(payload);

  }

  buildCustomEventPayload(type, metadata) {

    const payload = {
      ...this.buildDefaultPageEventPayload(),
      type,
      metadata: {
        ...metadata,
        userType: this.userType
      }
    };

    return payload;

  }

}

export default PageViewObserver;